import React, { createContext, ReactNode, useEffect, useState } from "react";
import { endPoints, getData } from "../utilities/networkCalls";
import config, { mantleWidgetId, userDotComKey } from "../config";
import { AppProvider as DiscountsProvider } from "@shopify/discount-app-components";
import { TStoreContext } from "./types";
import usePlansHook from "../hooks/usePlansHook";
import useGlobalSettingsHook from "../hooks/useGlobalSettingsHook";
import { FONTS, injectFonts } from "../utilities/fonts.tsx";
import useThemeColorsHook from "../hooks/useThemeColorsHook.ts";
import { LoadingScreen } from "../components/LoadingScreen/LoadingScreen.tsx";

export const AppConfigContext = createContext<TStoreContext>(null as any);

function AppConfigContextProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<TStoreContext>(null as any);

    const plansInfo = usePlansHook();
    const globalSettingsInfo = useGlobalSettingsHook();
    const themeColorsInfo = useThemeColorsHook();

    const [syncingOffersLoading, setSyncingOffersLoading] = useState<boolean>(false);
    const syncOffers = async () => {
        try {
            setSyncingOffersLoading(true);

            await getData({
                url: endPoints.syncOffers,
            });
            shopify.toast.show("All offers synced.");
        } catch (error) {
            shopify.toast.show("Offers synced failed.", {
                isError: true,
                duration: 2000,
            });
        } finally {
            setSyncingOffersLoading(false);
        }
    };

    useEffect(() => {
        const loadAppConfig = async () => {
            shopify.loading(true);
            const res = await getData({
                url: `/${shopify.config.apiKey}/config`,
            });
            shopify.loading(false);
            if (res.status === 200) {
                setState({
                    plansInfo,
                    globalSettingsInfo,
                    themeColorsInfo,
                    syncOffers,
                    syncingOffersLoading,
                    ...res.data,
                    shop: shopify.config.shop.split(".")[0],
                    email: res?.data?.email || "",
                });

                window.init_kefi_chat(
                    res?.data?.user_id || "",
                    res?.data?.name || "",
                    shopify?.config?.shop || "",
                    res?.data?.email || "",
                );

                // Intercom({
                //     app_id: iComKey,
                //     email: res?.data?.email || "",
                //     user_id: "Bundles-" + shopify?.config?.shop,
                //     name: res?.data?.name?.split(" ")[0] || shopify?.config?.shop?.split(".")[0],
                //     hide_default_launcher: false,
                //     total_bars: 0,
                //     owner: shopify?.config?.shop,
                //     company: {
                //         company_id: "2",
                //         name: shopify?.config?.shop,
                //     },
                // });

                // window.UE?.resetAuth({
                //     apiKey: userDotComKey,
                //     user_id: shopify.config.shop,
                //     name: res?.data?.name,
                //     app_name: "Bundles",
                //     store_name: shopify.config.shop?.split(".")[0],
                //     email: res?.data?.email,
                //     shop: shopify?.config?.shop,
                //     plan: res?.data?.subscription_info?.plan_name || "No Plan",
                //     install_date: res?.data?.install_date,
                // });

                // initMantleChat(res?.data?.mantleJwtToken);

                config.appName = res.data.appName;
                sessionStorage.setItem("showOnboarding", res?.data?.show_onboarding ? "1" : "0");
                localStorage.setItem("offerWorkingProperly", res?.data?.offer_working_properly ? "1" : "0");
                localStorage.setItem("isExtensionEnableOnboarding", res?.data?.isExtensionEnableOnboarding ? "1" : "0");
                if (
                    res?.data?.subscription_info == null ||
                    (typeof res?.data?.subscription_info === "object" &&
                        Object.keys(res.data.subscription_info).length === 0)
                ) {
                    localStorage.setItem("hasOfferCreated", "0");
                }
                injectFonts(FONTS);
            }
        };

        loadAppConfig();
    }, []);

    useEffect(() => {
        if (!plansInfo.loadingPlans) setState((ps) => ({ ...ps, plansInfo }));
    }, [plansInfo.loadingPlans, plansInfo.isExhausted]);

    useEffect(() => {
        if (!globalSettingsInfo.loading) setState((ps) => ({ ...ps, globalSettingsInfo }));
    }, [globalSettingsInfo.globalSettings, globalSettingsInfo.loading]);

    useEffect(() => {
        setState((ps) =>
            ps
                ? {
                      ...ps,
                      syncingOffersLoading,
                  }
                : ps,
        );
    }, [syncingOffersLoading]);

    useEffect(() => {
        if (!themeColorsInfo.loading) {
            setState((ps) => ({ ...ps, themeColorsInfo }));
        }
    }, [themeColorsInfo.themeColors, themeColorsInfo.loading]);

    useEffect(() => {
        if (state) {
            plansInfo.getAllPlans();
            globalSettingsInfo.fetchSettings();
            if (state?.sft) {
                themeColorsInfo.fetchThemeColors(state?.sft, state?.user_id);
            }
        }
    }, [!!state]);

    return (
        <AppConfigContext.Provider value={state}>
            {state ? (
                <DiscountsProvider locale="en-US" ianaTimezone={state.iana_timezone || "asia/calcutta"}>
                    {children}
                </DiscountsProvider>
            ) : (
                <LoadingScreen />
            )}
        </AppConfigContext.Provider>
    );
}

export default AppConfigContextProvider;
